import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { NgIf } from '@angular/common';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subject, takeUntil } from 'rxjs';
import { NotificationData } from '../../../services/notification.service';
import { ModalStateService } from '../../../services/modal-state.service';
import { WayPurchaseModalStateService } from '../../../components/way-purchase-modal/services/way-purchase-modal-state.service';
import { OrdersService } from '../../../orders/services/orders.service';
import { openUserReopenNotificationModal } from '../../utils/notification-modal-open.util';
import {
  buildLaneAutoRedeemToastDetail,
  buildLaneAutoRedeemToastSummary,
  isLaneGateOpening,
  LANE_AUTO_REDEEM_SUCCESS_TOAST_KEY,
} from '../../utils/lane-auto-redeem-toast.util';
import { LaneAutoRedeemToastService } from './lane-auto-redeem-toast.service';
import { ToastTemplateDirective } from '../toast/toast-template.directive';

/**
 * Keyed toast for Way LPR auto-redeem success (gate already opening). Supplies the
 * custom toast body to ToastContainerComponent via ToastTemplateDirective.
 * Open details reuses the existing order-success / active-subscription modal.
 */
@Component({
  selector: 'app-lane-auto-redeem-toast',
  standalone: true,
  imports: [NgIf, ToastTemplateDirective],
  templateUrl: './lane-auto-redeem-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lane-auto-redeem-toast.component.scss',
})
export class LaneAutoRedeemToastComponent implements OnInit, OnDestroy {
  readonly toastKey = LANE_AUTO_REDEEM_SUCCESS_TOAST_KEY;

  private readonly destroy$ = new Subject<void>();
  private readonly laneAutoRedeemToastService = inject(LaneAutoRedeemToastService);
  private readonly messageService = inject(NotificationService);
  private readonly ordersService = inject(OrdersService);
  private readonly modalStateService = inject(ModalStateService);
  private readonly wayPurchaseModalState = inject(WayPurchaseModalStateService);

  ngOnInit(): void {
    this.laneAutoRedeemToastService.show$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => this.show(notification));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOpenDetails(message: { data?: { notification?: NotificationData } }): void {
    const notification = message?.data?.notification;
    if (!notification) {
      return;
    }
    this.messageService.clear(this.toastKey);
    // Explicit user action — do not pass ActiveSubscriptionGateService (would skip modal
    // and re-open the gate for automatic active-subscription notifications).
    openUserReopenNotificationModal(
      this.modalStateService,
      this.wayPurchaseModalState,
      notification
    );
  }

  private show(notification: NotificationData): void {
    this.messageService.add({
      key: this.toastKey,
      severity: 'success',
      summary: buildLaneAutoRedeemToastSummary(notification),
      detail: buildLaneAutoRedeemToastDetail(notification),
      life: 10000,
      styleClass: 'lane-auto-redeem-success-toast',
      // CWS-1053: resolved here rather than in the template so the check stays one
      // testable function instead of a data-shape expression in HTML.
      data: { notification, gateOpening: isLaneGateOpening(notification) },
    });
    // Same refresh path as order-success "Got it" — reloads with current Orders filters when open.
    this.ordersService.refreshOrders();
  }
}
